import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Trophy, ChevronRight, X, CalendarDays, MessageCircle, CheckCircle2 } from 'lucide-react'
import { api, resolveMediaUrl } from '../services/api'
import { TuCoinIcon } from './TuCoinIcon'
import { RichDesc } from './challengeDesc'
import { CHALLENGE_COMPLETION_TUCOIN } from '@tfa/shared'
import { useFeatureAccess } from '../hooks/useFeatureAccess'

const F = 'Roboto, sans-serif'

function pluralizeDays(n: number): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'день'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дня'
  return 'дней'
}

/**
 * Компактный блок активного челленджа. Рендерится на Главной (variant=home)
 * и в Дневнике над тренировкой (variant=diary). Тап → шторка с полным
 * описанием и кнопкой в общий чат челленджей. Если активного нет — null.
 */
export function ActiveChallengeBlock({ variant }: { variant: 'home' | 'diary' }) {
  const [open, setOpen] = useState(false)
  // #597: у тарифа без челленджей (Программа тренировок) карточка в дневнике
  // показывалась — Главная проверяла фичу, а этот блок нет. Проверка здесь,
  // чтобы гейт был везде, где блок выводится.
  const challengesOpen = useFeatureAccess().has('challenges')

  const { data } = useQuery({
    queryKey: ['challenges-v2'],
    queryFn: () => api.getChallengesV2(),
    refetchInterval: 120_000,
    enabled: challengesOpen,
  })

  const active = data?.items.find((c) => c.status === 'ACTIVE')
  if (!challengesOpen || !active) return null

  // Кнопка чата → топик челленджа, если задан; иначе общий чат челленджей.
  const workingChatLink = (active as any).topicLink || data?.workingChatLink || null
  const photoSrc = resolveMediaUrl(active.photoUrl)
  const totalXp = active.xpPerCompletion * active.durationDays
  // Сегодня уже сдан и зачтён — «сдано, награды получены» до конца дня.
  const doneToday = active.todayCount >= active.dailyLimit

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          border: doneToday ? '1.5px solid rgba(52,199,89,0.35)' : '1.5px solid rgba(74,108,247,0.28)',
          borderRadius: variant === 'home' ? 16 : 20,
          background: doneToday
            ? 'linear-gradient(135deg, rgba(52,199,89,0.12) 0%, rgba(52,199,89,0.05) 100%)'
            : 'linear-gradient(135deg, rgba(74,108,247,0.10) 0%, rgba(116,185,255,0.07) 100%)',
          padding: variant === 'home' ? '12px 14px' : '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily: F,
        }}
      >
        <div style={{
          flexShrink: 0,
          width: variant === 'home' ? 36 : 42,
          height: variant === 'home' ? 36 : 42,
          borderRadius: 12,
          background: doneToday ? 'linear-gradient(135deg, #1C9E44, #34C759)' : 'linear-gradient(135deg, #1A3A5C, #4A6CF7)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {doneToday ? <CheckCircle2 size={variant === 'home' ? 18 : 22} /> : <Trophy size={variant === 'home' ? 18 : 22} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: doneToday ? '#1C9E44' : '#4A6CF7', marginBottom: 2 }}>
            {doneToday ? 'ЧЕЛЛЕНДЖ · СДАНО СЕГОДНЯ' : 'АКТИВНЫЙ ЧЕЛЛЕНДЖ'}
          </div>
          <div style={{ fontSize: variant === 'home' ? 14 : 15, fontWeight: 700, color: '#1C2D41', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {active.name}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 11, color: '#6B7B8D', alignItems: 'center', flexWrap: 'wrap' }}>
            {doneToday ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#1C9E44', fontWeight: 600 }}>
                Награды получены · +{active.xpPerCompletion} XP
              </span>
            ) : (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <CalendarDays size={11} /> {active.daysCompleted}/{active.durationDays} {pluralizeDays(active.durationDays)}
                </span>
                {/* #474: монета одна и за ВЕСЬ челлендж. Здесь стояло
                    maxTucoin (7 — по монете за день), а начислений TuCoin за
                    челленджи не было вовсе: клиент отработал задание и не
                    увидел ничего. */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#AF52DE', fontWeight: 600 }}>
                  <TuCoinIcon size={11} /> +{CHALLENGE_COMPLETION_TUCOIN}
                </span>
              </>
            )}
          </div>
        </div>
        <ChevronRight size={18} color="#9AA5B4" style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'flex-end' }} onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxHeight: '85vh', overflowY: 'auto', background: 'white', borderRadius: '24px 24px 0 0', padding: 20, boxShadow: '0 -8px 40px rgba(0,0,0,0.18)', fontFamily: F }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41', margin: 0 }}>{active.name}</h2>
              <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'rgba(0,0,0,0.06)', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={16} /></button>
            </div>

            {photoSrc && (
              <img src={photoSrc} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 12, marginBottom: 12 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, background: '#1A3A5C', color: 'white', height: 24, borderRadius: 12, padding: '0 10px' }}>
                Всего +{totalXp} XP
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7B8D' }}>
                <CalendarDays size={12} /> {active.daysCompleted}/{active.durationDays} {pluralizeDays(active.durationDays)}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#AF52DE' }}>
                <TuCoinIcon size={12} /> +{CHALLENGE_COMPLETION_TUCOIN} Тукоин за все {active.durationDays} {pluralizeDays(active.durationDays)}
              </span>
            </div>

            <RichDesc html={active.description} className="challenge-desc" style={{ fontSize: 14 }} />

            {workingChatLink && (
              <a href={workingChatLink} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 14, fontWeight: 700, textDecoration: 'none', marginTop: 16 }}>
                <MessageCircle size={16} /> Открыть чат челленджа
              </a>
            )}
          </div>
        </div>
      )}
    </>
  )
}
